import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { validatePricingUpdate } from '../services/pricingValidationService';

function BulkPriceUpdate({ open, onClose, onUpdate }) {
  const [updateType, setUpdateType] = useState('');
  const [updateMethod, setUpdateMethod] = useState('percentage');
  const [value, setValue] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const updateTypes = [
    { value: 'cableBasePrices', label: 'Cable Base Prices' },
    { value: 'powerWireGaugePricing', label: 'Power Wire Gauge Pricing' },
    { value: 'wyeCablePricing', label: 'Wye Cable Pricing' },
    { value: 'panelMountPricing', label: 'Panel Mount Connectors' },
    { value: 'inlinePricing', label: 'Inline Connectors' },
    { value: 'finishingPricing', label: 'Finishing Options' },
    { value: 'laborCosts', label: 'Labor Costs' }
  ];

  const handlePreview = () => {
    try {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        throw new Error('Please enter a valid number');
      }

      if (updateMethod === 'percentage' && (numValue < -100 || numValue > 1000)) {
        throw new Error('Percentage must be between -100 and 1000');
      }

      if (updateMethod === 'fixed' && numValue < 0) {
        throw new Error('Fixed amount must be positive');
      }

      const previewData = {
        type: updateType,
        method: updateMethod,
        value: numValue,
        timestamp: new Date().toISOString()
      };

      const validationResult = validatePricingUpdate(previewData);
      if (!validationResult.isValid) {
        throw new Error(validationResult.errors.join(', '));
      }

      setPreview(previewData);
      setError('');
    } catch (err) {
      setError(err.message);
      setPreview(null);
    }
  };

  const handleApply = () => {
    if (preview) {
      onUpdate(preview);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Price Update</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Update Type</InputLabel>
                <Select
                  value={updateType}
                  onChange={(e) => setUpdateType(e.target.value)}
                  label="Update Type"
                >
                  {updateTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Update Method</InputLabel>
                <Select
                  value={updateMethod}
                  onChange={(e) => setUpdateMethod(e.target.value)}
                  label="Update Method"
                >
                  <MenuItem value="percentage">Percentage Change</MenuItem>
                  <MenuItem value="fixed">Fixed Amount Change</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={updateMethod === 'percentage' ? 'Percentage' : 'Amount'}
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                InputProps={{
                  endAdornment: updateMethod === 'percentage' ? '%' : '$'
                }}
              />
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}

            {preview && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Preview
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            {updateTypes.find(t => t.value === preview.type)?.label}
                          </TableCell>
                          <TableCell>
                            {preview.method === 'percentage' ? 'Percentage Change' : 'Fixed Amount Change'}
                          </TableCell>
                          <TableCell>
                            {preview.method === 'percentage' ? `${preview.value}%` : `$${preview.value}`}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handlePreview} variant="outlined">
          Preview
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          disabled={!preview}
        >
          Apply Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BulkPriceUpdate; 